import { DEFAULT_EMAIL_ADDRESS } from '../shared/constants';
import { IFormValues } from '../types/shared';

import { transformTemplate } from '../utils/string-utils';

export interface IRecipients {
  emailAddress: {
    address: string;
  };
}

export interface IMailTemplate {
  message: {
    toRecipients: IRecipients[];
    subject: string;
    body: {
      contentType: string;
      content: string;
    };
  };
}

export interface IErrorResponse {
  statusCode?: number;
  statusText?: string;
  url?: string;
  error?: string;
  message?: string;
  name?: string;
}

export interface ISuccessResponse {
  message: string;
}

export const getRecaptchaSiteKey = () => {
  return process.env.GATSBY_RECAPTCHA_SITEKEY || 'RECAPTCHA_SITEKEY';
};

export const getRecaptchaUrl = (token: string): string => {
  const backendPath = process.env.GATSBY_BACKEND_PATH
    ? `${process.env.GATSBY_BACKEND_PATH}/`
    : '';
  return `https://${process.env.GATSBY_EMAIL_SERVICE_HOST}/${backendPath}api/email/send/v1?g-recaptcha-response=${token}`;
};

export const submitWithReCaptchaToken = (
  token: string,
  emailTemplate: any,
  callback: (response: ISuccessResponse) => void,
  errorCallback: (response: IErrorResponse) => void
) => {
  const xhr: XMLHttpRequest = new XMLHttpRequest();

  xhr.open('POST', getRecaptchaUrl(token), true);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.onload = () => {
    if (xhr.status !== 200) {
      errorCallback(JSON.parse(xhr.response));
      console.error(`HTTP ${xhr.status}: ${xhr.statusText}`);
    } else {
      try {
        callback(JSON.parse(xhr.response));
      } catch (e) {
        errorCallback(JSON.parse(xhr.response));
        console.error(`HTTP ${xhr.status}: ${xhr.statusText}`);
        console.error(`Catch error: ${e}`);
      }
    }
  };
  xhr.onerror = () => {
    errorCallback(JSON.parse(xhr.response));
    console.error(`XML OnError. HTTP ${xhr.status}: ${xhr.statusText}`);
  };
  xhr.send(JSON.stringify(emailTemplate));
};

export const createGraphMailTemplate = (
  formResults: IFormValues,
  toAddresses: string[],
  mailSubject: string,
  body: { body: string }
): IMailTemplate => {
  const pattern = /\{.+?}/g;
  const emailBody = transformTemplate(body.body, formResults);
  const filterEmptyValueIDsEmailBody = emailBody.replace(pattern, '-');

  const isNotProduction =
    process.env.GATSBY_STAGE === 'development' ||
    process.env.GATSBY_STAGE === 'acceptance' ||
    process.env.GATSBY_STAGE === 'local';

  const mailAddresses = !isNotProduction
    ? toAddresses.map((email) => {
        return {
          emailAddress: {
            address: email,
          },
        };
      })
    : [
        {
          emailAddress: {
            address: DEFAULT_EMAIL_ADDRESS,
          },
        },
      ];

  const subject =
    process.env.GATSBY_STAGE === 'local'
      ? `LOCAL - Test: ${mailSubject}`
      : process.env.GATSBY_STAGE === 'development'
      ? `DEV - Test: ${mailSubject}`
      : process.env.GATSBY_STAGE === 'acceptance'
      ? `ACC - Test: ${mailSubject}`
      : mailSubject;

  return {
    message: {
      toRecipients: mailAddresses,
      subject,
      body: {
        contentType: 'text',
        content: filterEmptyValueIDsEmailBody,
      },
    },
  };
};

export const handleReCaptcha = (
  emailTemplate: IMailTemplate,
  callback: (response: ISuccessResponse) => void,
  errorCallback: (response: IErrorResponse) => void
) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.grecaptcha.ready(() => {
    window.grecaptcha
      .execute(`${getRecaptchaSiteKey()}`, {
        action: 'submit',
      })
      .then((token: string) => {
        submitWithReCaptchaToken(token, emailTemplate, callback, errorCallback);
      });
  });
};
